/**
 * Guard for the `vite:preloadError` recovery reload.
 *
 * Reloading fixes the common case — an old tab holding chunk hashes that a deploy removed.
 * It does NOT fix a preload that fails for a permanent reason (an unresolvable bare specifier
 * from an externalized dependency, a CDN that is down), and an unguarded reload then loops
 * forever: `/create/edit` reloaded ~3×/sec until the tab was unusable. One reload per cooldown
 * keeps the deploy recovery and caps the damage when the reload can't help.
 */
export const PRELOAD_RELOAD_KEY = "vite-preload-reloaded-at"
export const PRELOAD_RELOAD_COOLDOWN_MS = 30_000

interface IPreloadReloadStore {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

export const shouldReloadForPreloadError = (store: IPreloadReloadStore | undefined, now: number): boolean => {
  // No storage (private mode, blocked site data) — a single best-effort reload beats a dead page.
  if (!store) return true

  try {
    const stored = store.getItem(PRELOAD_RELOAD_KEY)
    const lastReloadAt = stored === null ? null : Number(stored)
    const isWithinCooldown =
      lastReloadAt !== null && Number.isFinite(lastReloadAt) && now - lastReloadAt < PRELOAD_RELOAD_COOLDOWN_MS
    if (isWithinCooldown) return false
    store.setItem(PRELOAD_RELOAD_KEY, String(now))
    return true
    // eslint-disable-next-line @aix/no-exception-swallowing -- Expected: storage access throws when site data is blocked; reload once rather than not at all
  } catch {
    return true
  }
}
